import CheckBox from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  ListSubheader,
  Paper,
  PaperProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { withoutConflictsFor } from "encamp-shared/src/hazards/hazardCategoryExclusivity";
import { HazardType } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import {
  Children,
  createContext,
  isValidElement,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { Control, Controller, FieldPath, FieldValues, useWatch } from "react-hook-form";

import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import {
  buildHazardCategoryOptions,
  HazardCategoryOption,
  STANDALONE_GROUP,
} from "./hazardCategoryOptions";

const OPTION_BASE_PADDING_PX = 16;
const OPTION_INDENT_PX = 24;
// A small Checkbox (9px padding around a 20px icon).
const OPTION_CHECKBOX_WIDTH_PX = 38;
// The listbox has 8px padding; the class header sits at -8 and is 48px tall.
const GROUP_HEADER_TOP_PX = -8;
const PARENT_OPTION_TOP_PX = 40;

/** Rendered options carry their tree position so the group renderer can nest them. */
type OptionTreeData = { "data-code"?: string; "data-parent"?: string; "data-subgroup"?: string };

function treeDataOf(node: ReactNode): OptionTreeData {
  return isValidElement<OptionTreeData>(node) ? node.props : {};
}

function keyOf(node: ReactNode, fallback: number): string | number {
  return isValidElement(node) && node.key !== null ? node.key : fallback;
}

/** Index just past the run of rendered descendants that follows `start`. */
function endOfDescendants(items: ReactNode[], start: number): number {
  const blockCodes = new Set<string>();
  const startCode = treeDataOf(items[start])["data-code"];
  if (startCode !== undefined) blockCodes.add(startCode);
  let end = start + 1;
  while (end < items.length) {
    const { "data-code": code, "data-parent": parent } = treeDataOf(items[end]);
    if (parent === undefined || !blockCodes.has(parent)) break;
    if (code !== undefined) blockCodes.add(code);
    end += 1;
  }
  return end;
}

const tint = (color: string) => `linear-gradient(${color}, ${color})`;

// A class-only node's sub-header, aligned with the text of a depth-0 option
// (base padding plus the checkbox) and pinned like a selectable parent row.
const PINNED_SUBHEADER_SX = {
  top: PARENT_OPTION_TOP_PX,
  zIndex: 1,
  lineHeight: "36px",
  pl: `${OPTION_BASE_PADDING_PX + OPTION_CHECKBOX_WIDTH_PX}px`,
  color: "text.primary",
  backgroundColor: "background.paper",
} as const;

// A pinned parent must stay opaque or the rows scrolling beneath it show
// through. MUI's listbox rules outrank a bare child selector, so every state
// restates the opaque base and layers the theme's translucent tint on top.
const PINNED_PARENT_SX = {
  position: "sticky",
  top: PARENT_OPTION_TOP_PX,
  zIndex: 1,
  "&, &.Mui-focused, &[aria-selected='true'], &[aria-selected='true'].Mui-focused": {
    backgroundColor: "background.paper",
  },
  "&.Mui-focused": {
    backgroundImage: (theme: Theme) => tint(theme.palette.action.hover),
  },
  "&[aria-selected='true']": {
    backgroundImage: (theme: Theme) => tint(theme.palette.action.selected),
  },
  "&[aria-selected='true'].Mui-focused": {
    backgroundImage: (theme: Theme) =>
      `${tint(theme.palette.action.hover)}, ${tint(theme.palette.action.selected)}`,
  },
} as const;

/** Index just past the run of options sharing `start`'s sub-header. */
function endOfSubgroup(items: ReactNode[], start: number, subgroup: string): number {
  let end = start + 1;
  while (end < items.length && treeDataOf(items[end])["data-subgroup"] === subgroup) {
    end += 1;
  }
  return end;
}

/**
 * Wraps each selectable parent option, or each run of options under a
 * class-only sub-header, in its own list so the parent row or sub-header
 * stays pinned under the class header while its children scroll, and
 * releases when the next sibling arrives.
 */
function nestUnderParents(children: ReactNode): ReactNode[] {
  const items = Children.toArray(children);
  const nested: ReactNode[] = [];
  let index = 0;
  while (index < items.length) {
    const subgroup = treeDataOf(items[index])["data-subgroup"];
    if (subgroup !== undefined) {
      const end = endOfSubgroup(items, index, subgroup);
      nested.push(
        <Box component="li" key={keyOf(items[index], index)} sx={{ listStyle: "none" }}>
          <ListSubheader component="div" sx={PINNED_SUBHEADER_SX}>
            {subgroup}
          </ListSubheader>
          <Box component="ul" sx={{ p: 0 }}>
            {items.slice(index, end)}
          </Box>
        </Box>
      );
      index = end;
      continue;
    }
    const end = endOfDescendants(items, index);
    if (end === index + 1) {
      nested.push(items[index]);
      index = end;
      continue;
    }
    nested.push(
      <Box component="li" key={keyOf(items[index], index)} sx={{ listStyle: "none" }}>
        <Box component="ul" sx={{ p: 0, "& > li:first-of-type": PINNED_PARENT_SX }}>
          {items.slice(index, end)}
        </Box>
      </Box>
    );
    index = end;
  }
  return nested;
}

/** Hover info icon beside a class name; clicks on it don't close the listbox or toggle the row. */
function ClassInfo({
  name,
  description,
  inOption = false,
}: {
  name: string;
  description: string;
  /** Hidden from assistive tech inside an option, where it would join the option's name. */
  inOption?: boolean;
}) {
  return (
    <Tooltip title={description} describeChild>
      <Box
        component="span"
        {...(inOption ? { "aria-hidden": true } : { role: "img", "aria-label": `About ${name}` })}
        sx={{ ml: 0.5, display: "inline-flex", color: "text.disabled" }}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => event.stopPropagation()}
      >
        <InfoOutlined sx={{ fontSize: "1rem" }} />
      </Box>
    </Tooltip>
  );
}

function HazardOptionContent({
  option,
  selected,
}: {
  option: HazardCategoryOption;
  selected: boolean;
}) {
  // The class header explains grouped options; standalone ones carry their own.
  const standaloneDescription = option.group === STANDALONE_GROUP ? option.description : null;
  return (
    <>
      <Checkbox
        icon={<CheckBoxOutlineBlank fontSize="small" />}
        checkedIcon={<CheckBox fontSize="small" />}
        checked={selected}
        size="small"
      />
      <Box>
        {option.optionLabel}
        {option.note && (
          <Typography variant="caption" component="div" color="text.secondary">
            {option.note}
          </Typography>
        )}
      </Box>
      {standaloneDescription && (
        <ClassInfo name={option.label} description={standaloneDescription} inOption />
      )}
    </>
  );
}

/** Categories a pick dropped for conflicting with it, which may be scrolled out of view. */
type Replacement = { added: string; removed: string[] };

function replacementMessage(
  replacement: Replacement,
  options: readonly HazardCategoryOption[]
): string {
  const labelOf = (code: string) => options.find((option) => option.code === code)?.label ?? code;
  const removed = replacement.removed.map(labelOf).join(", ");
  return `Removed ${removed}, which can't apply alongside ${labelOf(replacement.added)}.`;
}

const ReplacementNoteContext = createContext<string | null>(null);

// Popper.js tags the popup with its placement; the note goes on the side away
// from the input, so its arrival doesn't shift the options.
const NOTE_AWAY_FROM_INPUT_SX = {
  display: "flex",
  flexDirection: "column",
  "[data-popper-placement^='top'] > &": { flexDirection: "column-reverse" },
} as const;

/** The listbox's surface, with the replacement note at its far edge. */
function PaperWithReplacementNote({ children, sx, ...props }: PaperProps) {
  const note = useContext(ReplacementNoteContext);
  return (
    <Paper {...props} sx={[NOTE_AWAY_FROM_INPUT_SX, ...(Array.isArray(sx) ? sx : [sx])]}>
      {children}
      {note && (
        <Alert severity="info" role="status" sx={{ borderRadius: 0, py: 0 }}>
          {note}
        </Alert>
      )}
    </Paper>
  );
}

/**
 * Searchable multi-select over one hazard type's categories, grouped
 * by OSHA class. The form value is the selected category codes.
 */
export function HazardCategoryPicker<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  hazardType,
  label,
}: {
  name: TName;
  control: Control<TFieldValues>;
  hazardType: HazardType;
  label: string;
}) {
  const options = useMemo(() => buildHazardCategoryOptions(hazardType), [hazardType]);
  const descriptionByGroup = useMemo(
    () => new Map(options.map((option) => [option.group, option.description])),
    [options]
  );
  const formContext = useValidatingFormContext<TFieldValues>();
  const watchedCodes = useWatch({ control, name });
  const [replacement, setReplacement] = useState<Replacement | null>(null);
  const note = replacement && replacementMessage(replacement, options);

  // Keeps seed order regardless of the order codes were picked.
  const selectedOptions = (codes: readonly string[]) =>
    options.filter((option) => codes.includes(option.code));

  if (formContext?.metadata?.isDisplayOnly) {
    const codes: readonly string[] = watchedCodes ?? [];
    const text = selectedOptions(codes)
      .map((option) => option.label)
      .join(", ");
    return <DisplayOnlyField label={label} text={text || "None"} wrapValue />;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const codes: readonly string[] = field.value ?? [];
        return (
          <FormControl fullWidth>
            <ReplacementNoteContext.Provider value={note}>
              <Autocomplete<HazardCategoryOption, true, false, false>
                multiple
                disableCloseOnSelect
                onOpen={() => setReplacement(null)}
                slots={{ paper: PaperWithReplacementNote }}
                options={options}
                value={selectedOptions(codes)}
                onChange={(_event, value, reason, details) => {
                  const next = value.map((option) => option.code);
                  const added = reason === "selectOption" ? details?.option.code : undefined;
                  const kept = added ? withoutConflictsFor(next, added) : next;
                  const removed = next.filter((code) => !kept.includes(code));
                  setReplacement(added && removed.length ? { added, removed } : null);
                  field.onChange(kept);
                }}
                onBlur={field.onBlur}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderGroup={(params) => {
                  const description =
                    params.group === STANDALONE_GROUP ? null : descriptionByGroup.get(params.group);
                  return (
                    <li key={params.key}>
                      <ListSubheader
                        component="div"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontWeight: 600,
                          top: GROUP_HEADER_TOP_PX,
                          zIndex: 2,
                          backgroundColor: "background.paper",
                        }}
                      >
                        {params.group}
                        {description && <ClassInfo name={params.group} description={description} />}
                      </ListSubheader>
                      <ul style={{ padding: 0 }}>{nestUnderParents(params.children)}</ul>
                    </li>
                  );
                }}
                renderOption={(props, option, { selected }) => (
                  <li
                    {...props}
                    key={option.code}
                    data-code={option.code}
                    data-parent={option.parentCode ?? undefined}
                    data-subgroup={option.subgroup ?? undefined}
                    style={{
                      paddingLeft: `${OPTION_BASE_PADDING_PX + option.depth * OPTION_INDENT_PX}px`,
                    }}
                  >
                    <HazardOptionContent option={option} selected={selected} />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        {...tagProps}
                        key={key}
                        label={option.label}
                        size="small"
                        variant="outlined"
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    placeholder={codes.length ? undefined : "Search by class or category"}
                    error={error != null}
                  />
                )}
              />
            </ReplacementNoteContext.Provider>
            {codes.length > 0 && <FormHelperText>{codes.length} selected</FormHelperText>}
            <ErrorDisplay error={error} staticSpacing={false} />
          </FormControl>
        );
      }}
    />
  );
}
